import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { INavigationItem, INavigationSection } from './interfaces';
import { navigationItems, navigationFixedItems, navigationSections } from './navigation-items';
import { Injectable } from '@angular/core';
import { USER_MENU_FAVORITES } from '@app/shared/constants/localstorage-keys';
import { BrowserLocalStorageService } from '@app/shared/services/utils/browserLocalStorage.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { environment } from '@environments/environment';
import { EnvironmentNameEnum } from '@environments/environment.enum';
import { TranslateService } from '@ngx-translate/core';
import { TitleCasePipe } from '@angular/common';
import { excludeLangFromTitleCase } from './constants';
import { UserService } from '@app/admin-station/work-center/user/services/user.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  public sidenavState: Subject<boolean>;
  public selectedNav: Subject<INavigationItem>;
  public selectedSection$: Subject<string>;
  public ungroupedItems: INavigationItem[];
  private menuItems: INavigationItem[];
  private menuNavFixedItems: INavigationItem[];
  private navigationSections: INavigationSection[] = [];

  private menuShrinkedSubject = new BehaviorSubject<boolean>(false);
  readonly menuShrinked$: Observable<boolean> = this.menuShrinkedSubject.asObservable();

  menuFavoritesKey = 'menuFavorites';

  isShrinked: boolean;

  constructor(
    private authService: AuthService,
    private browserLocalStorageService: BrowserLocalStorageService,
    private utilService: UtilService,
    private translateService: TranslateService,
    private titleCasePipe: TitleCasePipe,
    private userService: UserService
  ) {
    this.sidenavState = new Subject<boolean>();
    this.selectedNav = new Subject<INavigationItem>();
    this.selectedSection$ = new Subject<string>();
    this.ungroupedItems = [];
    this.menuNavFixedItems = [...navigationFixedItems];
    this.buildNavigationSections();
    this.setFavoriteItemsFromLocalStorage();
  }

  buildNavigationSections(): void {
    this.menuItems = [...navigationItems];
    this.navigationSections = navigationSections.map((s) => ({
      ...s,
      items: navigationItems.filter((i) => i.section === s.type),
    }));
  }

  setFavoriteItemsFromLocalStorage() {
    const localFavorites = this.browserLocalStorageService.getItem(USER_MENU_FAVORITES);

    if (localFavorites) {
      [...localFavorites].forEach((favoriteItem) => {
        const index = this.menuItems.findIndex((mi) => mi.name === favoriteItem.name);
        this.menuItems[index] = {
          ...this.menuItems[index],
          isMarkedAsFavorite: true,
        };
        this.setFavoritesOnSections(favoriteItem);
      });
    } else {
      const favorites = this.authService.getDbLocalStorageKey(USER_MENU_FAVORITES);

if (favorites) {
  let parsedFavorites;
  try {
    parsedFavorites = JSON.parse(favorites.value);
  } catch (e) {
    parsedFavorites = [];
  }
  if (Array.isArray(parsedFavorites)) {
    parsedFavorites.forEach((favoriteItem) => {
      const index = this.menuItems.findIndex((mi) => mi.name === favoriteItem.name);
      this.menuItems[index] = {
        ...this.menuItems[index],
        isMarkedAsFavorite: true,
        favoriteSelectionOrder: favoriteItem.favoriteSelectionOrder,
      };
      this.setFavoritesOnSections(favoriteItem);
    });
  }
}
    }
  }

  public getItemByUrl(url: string, navigationItems: INavigationItem[]): void {
    for (const navItem of navigationItems) {
      if (url === 'work-center') this.selectedSection$.next('home');
      if (navItem.routerLink === url || url.includes(navItem.routerLink)) {
        this.selectedNav.next(navItem);
        this.selectedSection$.next(navItem.section ? navItem.section : navItem.routerLink);
        return;
      } else if (navItem.items) {
        this.getItemByUrl(url, navItem.items);
      }
    }
  }

  public setAllNavigationItems(navItems: INavigationItem[]): void {
    this.ungroupedItems.push(...navItems);

    for (const navItem of navItems) {
      if (navItem.items) {
        this.setAllNavigationItems(navItem.items);
      }
    }
  }

  // Check if itself and parent (or grandparent) are selected
  public isSelectedItem(currentItem: INavigationItem, selectedItem: INavigationItem): boolean {
    if (!currentItem.routerLink) {
      return false;
    }

    if (!this.ungroupedItems || this.ungroupedItems.length === 0) {
      this.setAllNavigationItems(navigationItems);
    }

    if (currentItem.routerLink === selectedItem.routerLink) {
      // Itself
      return true;
    } else if (currentItem.routerLink === selectedItem.parentLink) {
      // parent
      return true;
    } else if (selectedItem.parentLink) {
      // grand & grand-grand... parent
      const foundItem = this.ungroupedItems.find((v) => v.routerLink === selectedItem.parentLink);
      return this.isSelectedItem(currentItem, foundItem);
    }
  }

  onMenuShrink(openMenu?: boolean) {
    if (openMenu) this.isShrinked = false;
    else this.isShrinked = !this.isShrinked;

    this.menuShrinkedSubject.next(this.isShrinked);
  }

  get menuNavSections(): INavigationSection[] {
    return [...this.navigationSections];
  }

  get menuNavItems(): INavigationItem[] {
    return [...this.menuItems];
  }

  get menuNavFixedItemsValue(): INavigationItem[] {
    return [...this.menuNavFixedItems];
  }

  get favoriteMenuItems(): INavigationItem[] {
    return [...this.menuItems.filter((menuItem) => menuItem.isMarkedAsFavorite)].sort(
      (it1, it2) => it1.favoriteSelectionOrder - it2.favoriteSelectionOrder
    );
  }

  setFavoritesOnSections(menuItem: INavigationItem) {
    // reflect favorites on section items
    this.navigationSections = this.navigationSections.map((s) => ({
      ...s,
      items: s.items.map((si) => {
        const isCurrentItem = si.name === menuItem.name;
        return isCurrentItem ? menuItem : si;
      }),
    }));
  }

  saveFavorite(menuItem: INavigationItem) {
    const index = this.menuItems.findIndex((mi) => mi.name === menuItem.name);
    const favoriteSelectionOrder = this.favoriteMenuItems.length + 1;
    this.menuItems[index] = { ...menuItem, favoriteSelectionOrder };

    this.setFavoritesOnSections(menuItem);

    this.browserLocalStorageService.setItem(USER_MENU_FAVORITES, this.favoriteMenuItems);

    this.browserLocalStorageService
      .saveAPILocalStorage(USER_MENU_FAVORITES, JSON.stringify(this.favoriteMenuItems))
      .subscribe(() => { });
  }

  public sectionItemIdentifyer = (index: number, item: INavigationItem) => {
    return item.section || item.routerLink;
  };

  public navNameTranslation(name: string): string {
    const translatedName = this.translateService.instant(name);
    const dontUserTitleCase = excludeLangFromTitleCase
      .some(lang => this.userService.getUserLoginData()?.defaultLanguage.toLowerCase().includes(lang));

    return dontUserTitleCase
      ? translatedName : this.titleCasePipe.transform(translatedName);
  }
}
